import React from 'react';
import _ from 'lodash';

import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  styled
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  fields,
  Form,
  LoadingOverlay
} from 'iron-forge-react-modules';

import { BaseWidget } from 'modules';
import { DateTime } from 'luxon';
import { useForm } from 'react-hook-form';
import * as dataService from './dataService';
import * as types from './types';



const DateFieldController = fields.DateField.genControllerComponent<types.IFormState>();



const RootBox = styled(Box)(({ theme }) => ({
  margin: theme.spacing(2)
}));

export const FC: React.FC = () => {
  const form = useForm<types.IFormState>({
    defaultValues: {
      startDate: DateTime.now().startOf('day').toJSDate(),
      endDate: DateTime.now().toJSDate()
    }
  });

  const [fnQuery, queryTuple] = dataService.useQuery();



  React.useEffect(
    () => {
      fnQuery({
        startDate: DateTime.now().startOf('day').toJSDate(),
        endDate: DateTime.now().toJSDate(),
        intervalMinutes: 60
      });
    },
    []
  );



  const domain = React.useMemo(
    () => {
      const min = DateTime.fromJSDate(form.getValues('startDate')).toFormat('MM-dd HH:mm');
      const max = DateTime.fromJSDate(form.getValues('endDate')).toFormat('MM-dd HH:mm');

      return [
        min,
        max
      ];
    },
    [form]
  );


  const daemonTimeSeries = React.useMemo(
    () => {
      return _.map(queryTuple.data.daemonTimeSeries, (series, index) => (
        <Grid
          key={index}
          item
          xs={6}>
          <BaseWidget.FC title={series.title}>
            <ResponsiveContainer
              width="100%"
              height={256}>
              <LineChart
                layout='horizontal'
                data={series.series}>
                <CartesianGrid />
                <XAxis
                  allowDataOverflow
                  dataKey="x"
                  domain={domain} />
                <YAxis label="(s)" />
                <Line dataKey="y" fill="#8884d8" />
                <Tooltip />
              </LineChart>
            </ResponsiveContainer>
          </BaseWidget.FC>
        </Grid>
      ));
    },
    [
      queryTuple.data,
      domain
    ]
  );

  return (
    <RootBox>
      <LoadingOverlay.FC active={queryTuple.loading} />

      <Grid
        container
        spacing={2}>
        <Grid
          item
          xs={4}>
          <Card>
            <Form.FC onSubmit={form.handleSubmit(fnQuery)}>
              <CardContent>
                <DateFieldController
                  label="Start"
                  formControl={form.control}
                  fieldKey="startDate" />

                <DateFieldController
                  label="End"
                  formControl={form.control}
                  fieldKey="endDate" />

                <Button type="submit">
                  Submit
                </Button>
              </CardContent>
            </Form.FC>
          </Card>
        </Grid>

        <Grid
          item
          xs={12}>
          <BaseWidget.FC title={`Order Count (${_.sumBy(queryTuple.data.orderCountTimeSeries, s => s.y)})`}>
            <ResponsiveContainer
              width="100%"
              height={256}>
              <BarChart
                layout='horizontal'
                data={queryTuple.data.orderCountTimeSeries}>
                <CartesianGrid />
                <XAxis
                  dataKey="x"
                  domain={domain}
                  allowDataOverflow />
                <YAxis />
                <Bar dataKey="y" fill="#8884d8" />

                <Tooltip />
              </BarChart>
            </ResponsiveContainer>
          </BaseWidget.FC>
        </Grid>

        {daemonTimeSeries}
      </Grid>
    </RootBox>
  );
};
